import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/formatApiError';
import { EditorTopNav } from '@/components/editorViewTabs/EditorTopNav';
import { NodeWorkflowCanvas } from '@/components/nodeWorkflow/NodeWorkflowCanvas';
import {
  loadStoryRepresentation,
  clearLegacyWorkflowGraph,
} from '@/lib/storyWorkflowSync';
import { agentCreatePath } from '@/lib/creationPlan';

/**
 * ComfyUI-style node workflow — same story as Scene View & Editor View.
 * Routes:
 *   /narration/:projectId/workflow
 *   /stories/:storyId/workflow
 */
export function NodeWorkflowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, storyId } = useParams();
  const isNarration = location.pathname.startsWith('/narration/');
  const mode = isNarration ? 'narration' : 'story';
  const id = isNarration ? projectId : storyId;

  const [title, setTitle] = useState('');
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      clearLegacyWorkflowGraph(mode, id);
      const rep = await loadStoryRepresentation(mode, id);
      setTitle(rep.title);
      setScenes(rep.scenes);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load story for workflow'));
      navigate(isNarration ? agentCreatePath({ flowId: 'narration' }) : '/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, mode, isNarration, navigate]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Refresh when returning to this tab (visibility) so Scene/Editor edits appear
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') reload();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [reload]);

  return (
    <div
      className="flex flex-col w-full h-screen bg-[#070b0e] text-slate-300 overflow-hidden"
      data-testid="node-workflow-page"
    >
      <EditorTopNav
        active="workflow"
        mode={mode}
        projectId={projectId}
        storyId={storyId}
        title={title}
        loading={loading}
        subtitle={
          !loading
            ? `${scenes.length} scene${scenes.length === 1 ? '' : 's'}`
            : ''
        }
      />

      <div className="flex-1 min-h-0 flex flex-col">
        <NodeWorkflowCanvas
          mode={mode}
          projectId={id}
          title={title}
          scenes={scenes}
          onScenesChange={setScenes}
          loading={loading}
          onReload={reload}
        />
      </div>
    </div>
  );
}

export default NodeWorkflowPage;
